import { useState, useEffect, useRef } from 'react';
import Editor from '@monaco-editor/react';
import { AgGridReact } from 'ag-grid-react';
import { AllEnterpriseModule } from 'ag-grid-enterprise';
import 'ag-grid-enterprise';
import type { ColDef } from 'ag-grid-community';
import { Play, Plus, Trash2, Terminal, Copy, AlignLeft, Undo2, Redo2 } from 'lucide-react';
import { format } from 'sql-formatter';
import toast from 'react-hot-toast';
import { fetchWorkbenchWorkspaces, saveWorkbenchWorkspaces, executeWorkbenchQuery, fetchWorkbenchSchema, type WorkbenchWorkspace } from '@/api/monitoring';

function createWorkspaceId(): string {
  const maybeCrypto = globalThis.crypto as { randomUUID?: () => string } | undefined;
  if (maybeCrypto?.randomUUID) return maybeCrypto.randomUUID();
  return `ws_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

export default function SqlWorkbenchTab() {
  const [workspaces, setWorkspaces] = useState<WorkbenchWorkspace[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  type QueryResultTab = {
    id: string;
    rows: Record<string, unknown>[];
    columns: ColDef[];
    commandInfo: { command: string; rowCount: number; executionTime: number };
  };
  const [resultTabs, setResultTabs] = useState<QueryResultTab[]>([]);
  const [activeResultTab, setActiveResultTab] = useState<string>('');
  const gridRef = useRef<AgGridReact>(null);
  const editorRef = useRef<any>(null);
  const completionProviderRef = useRef<any>(null);
  const [editorHeight, setEditorHeight] = useState(300);
  const [isResizing, setIsResizing] = useState(false);
  const [schema, setSchema] = useState<Record<string, string[]>>({});

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;
      setEditorHeight((prev) => Math.max(100, prev + e.movementY));
    };

    const handleMouseUp = () => {
      if (isResizing) setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = 'none';
    } else {
      document.body.style.userSelect = '';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  // Load workspaces and schema on mount
  useEffect(() => {
    Promise.all([
      fetchWorkbenchWorkspaces(),
      fetchWorkbenchSchema()
    ]).then(([wsData, schemaData]) => {
      if (wsData && wsData.length > 0) {
        setWorkspaces(wsData);
        setActiveId(wsData[0].id);
      } else {
        const initial = { id: createWorkspaceId(), name: 'Workspace 1', query: 'SELECT * FROM power_plant LIMIT 10;', limit: 200 };
        setWorkspaces([initial]);
        setActiveId(initial.id);
      }
      setSchema(schemaData || {});
      setLoading(false);
    }).catch(err => {
      toast.error('Veriler yüklenemedi: ' + err.message);
      setLoading(false);
    });
  }, []);

  const activeWorkspace = workspaces.find(w => w.id === activeId);

  // Auto save when workspaces change
  useEffect(() => {
    if (loading) return;
    const timer = setTimeout(() => {
      saveWorkbenchWorkspaces(workspaces).catch(() => {});
    }, 1500);
    return () => clearTimeout(timer);
  }, [workspaces, loading]);

  const handleAddWorkspace = () => {
    const newWs: WorkbenchWorkspace = {
      id: createWorkspaceId(),
      name: `Workspace ${workspaces.length + 1}`,
      query: 'SELECT * FROM ',
      limit: 200,
    };
    setWorkspaces([...workspaces, newWs]);
    setActiveId(newWs.id);
    setResultTabs([]);
    setActiveResultTab('');
  };

  const handleRemoveWorkspace = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (workspaces.length === 1) {
      toast.error('En az 1 çalışma alanı olmalıdır.');
      return;
    }
    const newWs = workspaces.filter(w => w.id !== id);
    setWorkspaces(newWs);
    if (activeId === id) {
      setActiveId(newWs[0].id);
      setResultTabs([]);
      setActiveResultTab('');
    }
  };

  const updateActiveWorkspace = (updates: Partial<WorkbenchWorkspace>) => {
    if (!activeId) return;
    setWorkspaces(workspaces.map(w => w.id === activeId ? { ...w, ...updates } : w));
  };

  const executeQuery = async (overrideQuery?: string) => {
    if (!activeWorkspace) return;

    let queryToExecute: string | undefined = overrideQuery;

    if (!queryToExecute) {
      if (!activeWorkspace.query.trim()) return;
      queryToExecute = activeWorkspace.query.trim();
      if (editorRef.current) {
        const selection = editorRef.current.getSelection();
        if (selection && !selection.isEmpty()) {
          const selectedText = editorRef.current.getModel()?.getValueInRange(selection);
          if (selectedText && selectedText.trim()) {
            queryToExecute = selectedText.trim();
          }
        }
      }
    }

    if (!queryToExecute) return;

    setExecuting(true);
    setResultTabs([]);
    setActiveResultTab('');
    const start = performance.now();
    try {
      const results = await executeWorkbenchQuery(queryToExecute, activeWorkspace.limit ?? 200);
      const end = performance.now();

      const newTabs: QueryResultTab[] = results.map((res, index) => {
        let cols: ColDef[] = [];
        if (res.fields && res.fields.length > 0) {
          cols = res.fields.map(f => ({
            field: f,
            headerName: f,
            sortable: true,
            filter: true,
            resizable: true,
            valueFormatter: (params) => {
               if (typeof params.value === 'object' && params.value !== null) {
                 return JSON.stringify(params.value);
               }
               return params.value;
            }
          }));
        } else if (res.rows && res.rows.length > 0) {
          cols = Object.keys(res.rows[0]).map(k => ({
            field: k,
            headerName: k,
            sortable: true,
            filter: true,
            resizable: true,
            valueFormatter: (params) => {
               if (typeof params.value === 'object' && params.value !== null) {
                 return JSON.stringify(params.value);
               }
               return params.value;
            }
          }));
        }

        return {
          id: `Result ${index + 1}`,
          rows: res.rows || [],
          columns: cols,
          commandInfo: {
            command: res.command,
            rowCount: res.rowCount,
            executionTime: Math.round((end - start) / results.length)
          }
        };
      });

      setResultTabs(newTabs);
      if (newTabs.length > 0) {
        setActiveResultTab(newTabs[0].id);
      }

      toast.success('Sorgu başarıyla çalıştırıldı.');
    } catch (err: any) {
      toast.error('Sorgu hatası: ' + (err.response?.data?.error || err.message));
      setResultTabs([]);
    } finally {
      setExecuting(false);
    }
  };

  const handleFormat = () => {
    if (!editorRef.current) return;
    const editor = editorRef.current;
    const selection = editor.getSelection();
    if (selection && !selection.isEmpty()) {
      const selectedText = editor.getModel()?.getValueInRange(selection);
      if (selectedText) {
        try {
          const formatted = format(selectedText, { language: 'postgresql' });
          editor.executeEdits('format', [{ range: selection, text: formatted }]);
        } catch(e) { toast.error('Format hatası'); }
      }
    } else {
      const text = activeWorkspace?.query || '';
      if (text) {
        try {
          const formatted = format(text, { language: 'postgresql' });
          updateActiveWorkspace({ query: formatted });
        } catch(e) { toast.error('Format hatası'); }
      }
    }
  };

  const handleUndo = () => {
    if (editorRef.current) {
      editorRef.current.trigger('keyboard', 'undo', null);
    }
  };

  const handleRedo = () => {
    if (editorRef.current) {
      editorRef.current.trigger('keyboard', 'redo', null);
    }
  };

  if (loading) {
    return <div className="p-4 text-sm text-gray-500">Yükleniyor...</div>;
  }

  return (
    <div className="flex h-full gap-4 p-4 lg:p-6 bg-slate-50">

      {/* Workspace Sidebar */}
      <div className="flex flex-col w-56 flex-shrink-0 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between p-3 border-b border-gray-100 bg-gray-50/50">
          <span className="text-xs font-bold text-gray-500 uppercase">Çalışma Alanları</span>
          <button 
            onClick={handleAddWorkspace}
            className="p-1.5 hover:bg-gray-200 rounded text-gray-500 transition-colors"
            title="Yeni Ekle"
          >
            <Plus size={14} />
          </button>
        </div>
        <div className="flex flex-col overflow-y-auto p-2 gap-1 custom-scrollbar">
          {workspaces.map(w => (
            <div
              key={w.id}
              onClick={() => setActiveId(w.id)}
              className={`group flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors border ${
                activeId === w.id 
                  ? 'bg-blue-50 border-blue-200 text-blue-700 shadow-sm' 
                  : 'bg-transparent border-transparent text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Terminal size={14} className="flex-shrink-0" />
              <input 
                type="text" 
                value={w.name} 
                onChange={(e) => {
                   setWorkspaces(workspaces.map(ws => ws.id === w.id ? { ...ws, name: e.target.value } : ws));
                }}
                className={`bg-transparent border-none outline-none text-sm font-medium focus:ring-0 p-0 w-full min-w-0 ${activeId === w.id ? 'text-blue-700' : 'text-gray-600'}`}
                onClick={(e) => e.stopPropagation()}
              />
              <button 
                onClick={(e) => handleRemoveWorkspace(w.id, e)}
                className="text-gray-400 hover:text-rose-500 flex-shrink-0 opacity-50 hover:opacity-100 transition-opacity"
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {activeWorkspace && (
        <div className="flex flex-col flex-1 min-w-0 h-full gap-0 min-h-0">

          {/* Query Editor Area */}
          <div style={{ height: editorHeight }} className="flex flex-col bg-white rounded-xl shadow-sm border border-gray-200 p-1 flex-shrink-0">
            <div className="flex items-center justify-between p-2 border-b border-gray-100 bg-gray-50/50 rounded-t-lg">
              <div className="flex items-center gap-3">
                <span className="text-xs font-bold text-gray-500 uppercase">Sorgu Editörü</span>
                <div className="flex items-center gap-2">
                  <label className="text-xs text-gray-500">Row Limit:</label>
                  <input 
                    type="number" 
                    value={activeWorkspace.limit} 
                    onChange={(e) => updateActiveWorkspace({ limit: parseInt(e.target.value) || 200 })}
                    className="border border-gray-200 rounded px-2 py-0.5 text-xs w-20 outline-none focus:border-blue-500"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                 <button
                    onClick={handleUndo}
                    className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded"
                    title="Geri Al (Undo)"
                 >
                   <Undo2 size={16} />
                 </button>
                 <button
                    onClick={handleRedo}
                    className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded"
                    title="İleri Al (Redo)"
                 >
                   <Redo2 size={16} />
                 </button>
                 <button
                    onClick={handleFormat}
                    className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                    title="SQL Formatla"
                 >
                   <AlignLeft size={16} />
                 </button>
                 <button
                    onClick={() => {
                      navigator.clipboard.writeText(activeWorkspace.query);
                      toast.success('Kopyalandı');
                    }}
                    className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded"
                    title="Kopyala"
                 >
                   <Copy size={16} />
                 </button>
                 <button
                   onClick={() => executeQuery()}
                   disabled={executing || (!activeWorkspace.query.trim() && !editorRef.current?.getSelection()?.isEmpty() === false)}
                   className="flex items-center gap-1.5 px-4 py-1.5 bg-blue-600 text-white text-sm font-bold rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                 >
                   <Play size={16} fill="currentColor" /> {executing ? 'Çalışıyor...' : 'Çalıştır (F5)'}
                 </button>
              </div>
            </div>
            <div className="flex-1 w-full rounded-b-lg overflow-hidden border-t-0">
              <Editor
                height="100%"
                language="sql"
                theme="vs-dark"
                value={activeWorkspace.query}
                onChange={(val) => updateActiveWorkspace({ query: val || '' })}
                onMount={(editor, monaco) => {
                  editorRef.current = editor;

                  editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter, () => {
                    executeQuery();
                  });
                  editor.addCommand(monaco.KeyCode.F5, () => {
                    executeQuery();
                  });
                  editor.addAction({
                    id: 'describe-table',
                    label: 'Tabloyu İncele (Describe)',
                    contextMenuGroupId: 'navigation',
                    contextMenuOrder: 1.5,
                    run: () => {
                      const selection = editor.getSelection();
                      let tableName = '';
                      if (selection && !selection.isEmpty()) {
                        tableName = editor.getModel()?.getValueInRange(selection)?.trim() || '';
                      } else {
                        const position = editor.getPosition();
                        if (position) {
                          const word = editor.getModel()?.getWordAtPosition(position);
                          if (word) {
                            tableName = word.word;
                          }
                        }
                      }
                      if (tableName) {
                         const describeQuery = `SELECT column_name, data_type, character_maximum_length, is_nullable, column_default FROM information_schema.columns WHERE table_name = '${tableName}' ORDER BY ordinal_position`;
                         void executeQuery(describeQuery);
                      } else {
                         toast.error('Lütfen bir tablo adı seçin veya üzerine gelin.');
                      }
                    }
                  });

                  if (completionProviderRef.current) {
                    completionProviderRef.current.dispose();
                  }

                  const createProposals = (range: any, position: any, model: any) => {
                    const proposals: any[] = [];
                    const keywords = ['SELECT', 'FROM', 'WHERE', 'INSERT', 'UPDATE', 'DELETE', 'JOIN', 'LEFT JOIN', 'RIGHT JOIN', 'GROUP BY', 'ORDER BY', 'HAVING', 'LIMIT', 'OFFSET', 'COUNT', 'SUM', 'AVG', 'MIN', 'MAX', 'AS', 'ON'];

                    const textUntilPosition = model.getValueInRange({
                      startLineNumber: 1,
                      startColumn: 1,
                      endLineNumber: position.lineNumber,
                      endColumn: position.column
                    });

                    // Çok basit bir "önceki kelime" analizi: alias. veya tablo. ile mi başlıyor?
                    const match = textUntilPosition.match(/([a-zA-Z0-9_]+)\.$/);
                    let targetTable = '';

                    if (match) {
                      const aliasOrTable = match[1];
                      // Tablo ismini schema içinde bulmaya çalış
                      if (schema[aliasOrTable]) {
                        targetTable = aliasOrTable;
                      } else {
                        // Eğer alias ise, query içinde `tablo alias` eşleşmesi ara
                        const aliasRegex = new RegExp(`([a-zA-Z0-9_]+)\\s+${aliasOrTable}\\b`, 'i');
                        const aliasMatch = model.getValue().match(aliasRegex);
                        if (aliasMatch && schema[aliasMatch[1]]) {
                          targetTable = aliasMatch[1];
                        }
                      }
                    }

                    if (targetTable) {
                      // Sadece o tablonun kolonlarını getir
                      schema[targetTable].forEach(col => {
                        proposals.push({
                          label: col,
                          kind: monaco.languages.CompletionItemKind.Field,
                          insertText: col,
                          detail: `Column in ${targetTable}`,
                          range
                        });
                      });
                    } else {
                      keywords.forEach(kw => {
                        proposals.push({
                          label: kw,
                          kind: monaco.languages.CompletionItemKind.Keyword,
                          insertText: kw,
                          range
                        });
                      });

                      Object.entries(schema).forEach(([table, columns]) => {
                        proposals.push({
                          label: table,
                          kind: monaco.languages.CompletionItemKind.Class,
                          insertText: table,
                          detail: 'Table',
                          range
                        });

                        columns.forEach(col => {
                          proposals.push({
                            label: col,
                            kind: monaco.languages.CompletionItemKind.Field,
                            insertText: col,
                            detail: `Column in ${table}`,
                            range
                          });
                        });
                      });
                    }

                    return proposals;
                  };

                  completionProviderRef.current = monaco.languages.registerCompletionItemProvider('sql', {
                    triggerCharacters: ['.'],
                    provideCompletionItems: (model: any, position: any) => {
                      const word = model.getWordUntilPosition(position);
                      const range = {
                        startLineNumber: position.lineNumber,
                        endLineNumber: position.lineNumber,
                        startColumn: word.startColumn,
                        endColumn: word.endColumn
                      };
                      return { suggestions: createProposals(range, position, model) };
                    }
                  });
                }}
                options={{
                  minimap: { enabled: false },
                  fontSize: 14,
                  lineNumbers: 'on',
                  wordWrap: 'on',
                  scrollBeyondLastLine: false,
                  automaticLayout: true,
                }}
              />
            </div>
          </div>

          {/* SPLITTER */}
          <div 
            className="h-4 my-1 w-full cursor-row-resize flex items-center justify-center group flex-shrink-0"
            onMouseDown={(e) => {
               e.preventDefault();
               setIsResizing(true);
            }}
          >
             <div className="w-16 h-1.5 bg-gray-200 group-hover:bg-blue-400 rounded-full transition-colors" />
          </div>

          {/* Results Area */}
          <div className="flex flex-col flex-1 min-h-0 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            {resultTabs.length > 1 && (
               <div className="flex items-center gap-1 p-2 border-b border-gray-100 bg-gray-50 overflow-x-auto custom-scrollbar">
                 {resultTabs.map((tab) => (
                   <button
                     key={tab.id}
                     onClick={() => setActiveResultTab(tab.id)}
                     className={`px-4 py-1.5 text-xs font-bold rounded-lg transition-colors whitespace-nowrap ${
                       activeResultTab === tab.id
                         ? 'bg-blue-100 text-blue-700'
                         : 'text-gray-500 hover:bg-gray-200'
                     }`}
                   >
                     {tab.id} ({tab.commandInfo.rowCount})
                   </button>
                 ))}
               </div>
            )}

            {resultTabs.length > 0 && activeResultTab ? (
              <>
                <div className="flex items-center justify-between p-3 border-b border-gray-100 bg-gray-50/50">
                   <span className="text-xs font-bold text-gray-500 uppercase">{resultTabs.find(t => t.id === activeResultTab)?.id} Sonuçları</span>
                   <div className="flex gap-4 text-xs font-medium">
                     <span className="text-emerald-600">Command: {resultTabs.find(t => t.id === activeResultTab)?.commandInfo.command}</span>
                     <span className="text-blue-600">Rows: {resultTabs.find(t => t.id === activeResultTab)?.commandInfo.rowCount}</span>
                     <span className="text-gray-500">Time: {resultTabs.find(t => t.id === activeResultTab)?.commandInfo.executionTime}ms</span>
                   </div>
                </div>
                <div className="ag-theme-quartz flex-1 w-full h-full">
                  <AgGridReact
                    modules={[AllEnterpriseModule]}
                    key={activeResultTab}
                    ref={gridRef}
                    rowData={resultTabs.find(t => t.id === activeResultTab)?.rows}
                    columnDefs={resultTabs.find(t => t.id === activeResultTab)?.columns}
                    rowHeight={24}
                    headerHeight={28}
                    enableCellTextSelection={true}
                    enableRangeSelection={true}
                    sideBar={{
                      toolPanels: ['columns', 'filters'],
                      defaultToolPanel: ''
                    }}
                    statusBar={{
                      statusPanels: [
                        { statusPanel: 'agTotalAndFilteredRowCountComponent', align: 'left' },
                        { statusPanel: 'agSelectedRowCountComponent' },
                        { statusPanel: 'agAggregationComponent' }
                      ]
                    }}
                    defaultColDef={{ 
                      resizable: true, 
                      sortable: true, 
                      filter: true,
                      enableRowGroup: true,
                      enableValue: true,
                      enablePivot: true
                    }}
                  />
                </div>
              </>
            ) : (
              <div className="flex items-center justify-between p-3 border-b border-gray-100 bg-gray-50/50">
                 <span className="text-xs font-bold text-gray-500 uppercase">Sonuçlar</span>
              </div>
            )}
            {resultTabs.length === 0 && (
              <div className="flex items-center justify-center h-full text-gray-400 text-sm bg-gray-50/20">
                Sorguyu çalıştırdıktan sonra sonuçlar burada görünecektir.
              </div>
            )}
          </div>

        </div>
      )}
    </div>
  );
}
